import React, { useId, useState } from 'react';
import { Circle, History, Loader2, Maximize2, Minimize2, Pause, Play, Radio, RotateCcw, SkipBack, SkipForward, Square, Target, Video, Volume2, VolumeX } from 'lucide-react';
import './EZOPENControls.css';

interface EZOPENControlsProps {
  cameraName: string;
  mode: 'live' | 'playback';
  quality: 'hd' | 'sub';
  playing: boolean;
  paused: boolean;
  recording: boolean;
  loading: boolean;
  visible: boolean;
  fullscreen: boolean;
  autoPlayback: boolean;
  audioSupported: boolean;
  audioMuted: boolean;
  audioLoading: boolean;
  audioError: string | null;
  intercomControl?: React.ReactNode;
  storageType: 1 | 2;
  startTime: string;
  endTime: string;
  progress: number;
  eventPosition?: number;
  onPlay: () => Promise<void>;
  onPause: () => Promise<void>;
  onResume: () => Promise<void>;
  onStop: () => Promise<void>;
  onModeChange: (mode: 'live' | 'playback') => Promise<void>;
  onRecord: () => Promise<void>;
  onToggleAudio: () => void;
  onToggleFullscreen: () => void;
  onStorageChange: (value: 1 | 2) => void;
  onStartTimeChange: (value: string) => void;
  onEndTimeChange: (value: string) => void;
  onSeek: (percentage: number) => Promise<void>;
}

const formatTime = (value: string | number) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
};

const EZOPENControls: React.FC<EZOPENControlsProps> = props => {
  const id = useId();
  const [busy, setBusy] = useState(false);
  const [seekPreview, setSeekPreview] = useState<number | null>(null);
  const disabled = props.loading || busy;
  const progress = seekPreview ?? props.progress;
  const currentTime = new Date(props.startTime).getTime() +
    (new Date(props.endTime).getTime() - new Date(props.startTime).getTime()) * progress / 100;
  const status = props.loading ? 'Connecting' : !props.playing ? (props.progress >= 100 ? 'Recording ended' : 'Stopped') : props.paused ? 'Paused' : props.mode === 'live' ? 'Live stream' : 'Playing recording';
  const duration = new Date(props.endTime).getTime() - new Date(props.startTime).getTime();
  const eventPosition = props.eventPosition;
  const run = async (action: () => Promise<void>) => {
    if (disabled) return;
    setBusy(true);
    try {
      await action();
    } finally {
      setBusy(false);
    }
  };
  const seek = (value: number) => {
    setSeekPreview(null);
    void run(() => props.onSeek(value));
  };
  const primaryLabel = !props.playing ? (props.mode === 'live' ? 'Play stream' : props.progress >= 100 ? 'Replay recording' : 'Play recording') : props.mode === 'live' ? 'Stop stream' : props.paused ? 'Resume playback' : 'Pause playback';
  const primaryAction = !props.playing ? props.onPlay : props.mode === 'live' ? props.onStop : props.paused ? props.onResume : props.onPause;
  const PrimaryIcon = disabled ? Loader2 : !props.playing || props.paused ? Play : props.mode === 'live' ? Square : Pause;

  return (
    <>
      <div className="ezopen-monitor-heading" data-visible={props.visible}>
        <span className="ezopen-monitor-heading__icon"><Video size={18} strokeWidth={1.7} aria-hidden="true" /></span>
        <div>
          <span className="ezopen-monitor-heading__eyebrow">{props.mode === 'live' ? 'Live monitoring' : 'Recording playback'}</span>
          <span className="ezopen-monitor-heading__name">{props.cameraName}</span>
        </div>
      </div>
      {props.recording && <div className="ezopen-recording-indicator" role="status"><span />Recording</div>}

      <div className="ezopen-console" data-visible={props.visible}>
        <div className="ezopen-console__panel" role="group" aria-label="Camera controls">
          {props.mode === 'playback' && (
            <div className="ezopen-console__playback">
              {!props.autoPlayback && (
                <div className="ezopen-console__fields">
                  <div className="ezopen-console__field ezopen-console__storage">
                    <label htmlFor={`${id}-storage`}>Recording source</label>
                    <select id={`${id}-storage`} value={props.storageType} disabled={props.playing || disabled}
                      onChange={event => props.onStorageChange(Number(event.target.value) as 1 | 2)}>
                      <option value={1}>Cloud storage</option><option value={2}>Local storage</option>
                    </select>
                  </div>
                  <div className="ezopen-console__field">
                    <label htmlFor={`${id}-start`}>From</label>
                    <input id={`${id}-start`} type="datetime-local" step={1} value={props.startTime} disabled={props.playing || disabled}
                      onChange={event => props.onStartTimeChange(event.target.value)} />
                  </div>
                  <div className="ezopen-console__field">
                    <label htmlFor={`${id}-end`}>To</label>
                    <input id={`${id}-end`} type="datetime-local" step={1} value={props.endTime} disabled={props.playing || disabled}
                      onChange={event => props.onEndTimeChange(event.target.value)} />
                  </div>
                </div>
              )}
              <div className="ezopen-console__timeline">
                <div className="ezopen-console__time-labels"><span>{formatTime(currentTime)}</span><span>{formatTime(props.endTime)}</span></div>
                {eventPosition !== undefined && eventPosition >= 0 && eventPosition <= 100 && <span className="ezopen-console__event-marker" style={{ left: `${eventPosition}%` }} title="Event occurred here" aria-label="Event time marker" />}
                <input type="range" aria-label="Recording position" min={0} max={100} step={0.1} value={progress}
                  disabled={!props.playing || disabled} style={{ '--seek-progress': `${progress}%` } as React.CSSProperties}
                  onChange={event => setSeekPreview(Number(event.target.value))}
                  onPointerUp={event => seek(Number(event.currentTarget.value))}
                  onPointerCancel={() => setSeekPreview(null)}
                  onKeyUp={event => {
                    if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'PageUp', 'PageDown'].includes(event.key)) seek(Number(event.currentTarget.value));
                  }} />
              </div>
            </div>
          )}

          <div className="ezopen-console__toolbar">
            <div className="ezopen-console__transport">
              <button type="button" className="ezopen-console__button ezopen-console__button--primary" disabled={disabled}
                aria-label={primaryLabel} title={primaryLabel} onClick={() => void run(primaryAction)}>
                <PrimaryIcon size={18} strokeWidth={2} className={disabled ? 'ezopen-console__spinner' : undefined} aria-hidden="true" />
              </button>
              {props.mode === 'playback' && props.playing && (
                <>
                <button type="button" className="ezopen-console__button" aria-label="Back 10 seconds" title="Back 10 seconds"
                  disabled={disabled || !(duration > 0)} onClick={() => seek(Math.max(0, props.progress - 1000000 / duration))}><SkipBack size={17} aria-hidden="true" /></button>
                <button type="button" className="ezopen-console__button" aria-label="Forward 10 seconds" title="Forward 10 seconds"
                  disabled={disabled || !(duration > 0)} onClick={() => seek(Math.min(99.9, props.progress + 1000000 / duration))}><SkipForward size={17} aria-hidden="true" /></button>
                <button type="button" className="ezopen-console__button" aria-label="Stop playback" title="Stop playback"
                  disabled={disabled} onClick={() => void run(props.onStop)}><Square size={17} aria-hidden="true" /></button>
                <button type="button" className="ezopen-console__button" aria-label="Replay recording" title="Replay recording"
                  disabled={disabled} onClick={() => seek(0)}><RotateCcw size={17} aria-hidden="true" /></button>
                </>
              )}
            </div>

            {!props.autoPlayback && <div className="ezopen-console__modes" role="group" aria-label="Stream mode">
              <button type="button" aria-pressed={props.mode === 'live'} disabled={disabled || props.mode === 'live'}
                onClick={() => void run(() => props.onModeChange('live'))}><Radio size={15} aria-hidden="true" />Live</button>
              <button type="button" aria-pressed={props.mode === 'playback'} disabled={disabled || props.mode === 'playback'}
                onClick={() => void run(() => props.onModeChange('playback'))}><History size={15} aria-hidden="true" />Playback</button>
            </div>}

            <div className="ezopen-console__stream-status" role="status">
              <span className="ezopen-console__status-dot" data-active={props.playing && !props.paused} />
              <span>{status}</span><span className="ezopen-console__quality">{props.quality === 'hd' ? 'HD' : 'SD'}</span>
            </div>

            <div className="ezopen-console__actions">
              {props.mode === 'playback' && eventPosition !== undefined && eventPosition >= 0 && eventPosition <= 100 && <button type="button"
                className="ezopen-console__button ezopen-console__jump" disabled={disabled || !props.playing}
                onClick={() => seek(eventPosition)} aria-label="Jump to event" title="Jump to event"><Target size={17} aria-hidden="true" /><span>Event</span></button>}
              <button type="button" className="ezopen-console__button ezopen-console__record" data-recording={props.recording}
                aria-label={props.recording ? 'Stop recording' : 'Start recording'} title={props.recording ? 'Stop and save recording' : 'Record stream'}
                aria-pressed={props.recording} disabled={!props.playing || disabled} onClick={() => void run(props.onRecord)}>
                {props.recording ? <Square size={15} aria-hidden="true" /> : <Circle size={17} aria-hidden="true" />}
                <span>{props.recording ? 'Stop recording' : 'Record'}</span>
              </button>
              {props.audioSupported && <button type="button" className="ezopen-console__button" aria-pressed={!props.audioMuted}
                aria-label={props.audioLoading ? 'Enabling audio' : props.audioMuted ? 'Unmute audio' : 'Mute audio'}
                title={props.audioError || (props.audioMuted ? 'Unmute audio' : 'Mute audio')} disabled={props.audioLoading || !props.playing}
                onClick={props.onToggleAudio}>
                {props.audioLoading ? <Loader2 size={19} className="ezopen-console__spinner" aria-hidden="true" /> : props.audioMuted ? <VolumeX size={19} aria-hidden="true" /> : <Volume2 size={19} aria-hidden="true" />}
              </button>}
              {props.intercomControl}
              <span className="ezopen-console__divider" />
              <button type="button" className="ezopen-console__button" aria-label={props.fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                title={props.fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'} onClick={props.onToggleFullscreen}>
                {props.fullscreen ? <Minimize2 size={19} aria-hidden="true" /> : <Maximize2 size={19} aria-hidden="true" />}
              </button>
            </div>
          </div>
          {props.audioError && <p className="ezopen-console__audio-error" role="alert">{props.audioError}</p>}
        </div>
      </div>
    </>
  );
};

export default EZOPENControls;
